import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdClose } from 'react-icons/io';
import { formatInr } from '../../data/pricingPlans';
import { createOrder, loadRazorpay, openRazorpayCheckout, verifyPayment } from '../../lib/razorpayCheckout';

const initialForm = {
  name: '',
  email: '',
  mobile: '',
};

function CheckoutModal({ plan, onClose, onError }) {
  const navigate = useNavigate();
  const completedRef = useRef(false);
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    document.body.classList.add('menu-open');
    return () => document.body.classList.remove('menu-open');
  }, []);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus('');
    setIsSubmitting(true);

    try {
      const order = await createOrder({
        planId: plan.id,
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
      });

      await loadRazorpay();

      const checkout = openRazorpayCheckout({
        key: order.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Bright Study',
        description: `${plan.name} school ERP license`,
        order_id: order.order_id,
        prefill: {
          name: form.name.trim(),
          email: form.email.trim(),
          contact: form.mobile.trim(),
        },
        notes: {
          plan_id: plan.id,
        },
        theme: {
          color: '#015dbf',
        },
        modal: {
          ondismiss: () => {
            if (completedRef.current) return;
            setIsSubmitting(false);
            setStatus('Payment was cancelled. You can try again when you are ready.');
          },
        },
        handler: async (response) => {
          try {
            await verifyPayment({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });

            completedRef.current = true;
            navigate('/pricing/success', {
              replace: true,
              state: {
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                amount: plan.amount,
                planName: plan.name,
                name: form.name.trim(),
              },
            });
          } catch (error) {
            const message = error.message || 'Payment could not be verified.';
            setStatus(message);
            if (onError) onError(message);
            setIsSubmitting(false);
          }
        },
      });

      checkout.on('payment.failed', (response) => {
        const message = response.error?.description || 'Payment failed. Please try again.';
        setStatus(message);
        if (onError) onError(message);
        setIsSubmitting(false);
      });
    } catch (error) {
      const message = error.message || 'Could not start checkout. Please try again.';
      setStatus(message);
      if (onError) onError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pricing-modal" role="presentation" onClick={onClose}>
      <div
        className="pricing-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="pricing-modal-close" onClick={onClose} aria-label="Close checkout">
          <IoMdClose />
        </button>

        <p className="pricing-modal-kicker">{plan.code}</p>
        <h2 id="checkout-title">Buy {plan.name}</h2>
        <p className="pricing-modal-lead">
          Pay {formatInr(plan.amount)} securely with Razorpay. Enter your details to continue.
        </p>

        <form className="pricing-modal-form" onSubmit={onSubmit}>
          <label>
            Full name
            <input
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Rahul Kumar"
              required
              minLength={2}
              maxLength={120}
            />
          </label>
          <label>
            Email
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              placeholder="rahul@gmail.com"
              required
            />
          </label>
          <label>
            Mobile
            <input
              name="mobile"
              type="tel"
              inputMode="numeric"
              value={form.mobile}
              onChange={onChange}
              placeholder="9876543210"
              required
              pattern="[6-9][0-9]{9}"
              title="Enter a 10-digit Indian mobile number"
            />
          </label>

          {status ? <p className="pricing-modal-status">{status}</p> : null}

          <button type="submit" className="pricing-modal-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Opening Razorpay…' : `Pay ${formatInr(plan.amount)}`}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CheckoutModal;
